import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useHelp } from "@/context/HelpContext";
import { help, helpForRoles, searchHelp } from "@/lib/help";
import { openSupportQuestion } from "@/lib/supportContext";
import { errorMessage } from "@/lib/api";
import { HelpSections } from "@/components/HelpPanel";
import { PageHeader } from "@/components/ui/design-system";
import { Button } from "@/components/ui/button";

export function HelpPage() {
  const { user } = useAuth();
  const { articles, loading, failed, retry } = useHelp();
  const [all, setAll] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const audience = user?.roles.map((role) => role.name_hr).join(", ") ?? "";
  const visible = searchHelp(
    helpForRoles(articles, user?.roles.map((role) => role.code) ?? [], all),
    query,
  );
  async function download() {
    setBusy(true);
    setError("");
    try {
      const blob = await help.pdf(all);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "avm-upute.pdf";
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      setError(
        errorMessage(cause, "Upute za ispis nisu dostupne. Pokušajte ponovno."),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="help-page">
      <PageHeader title="Pomoć" />
      <Link className="help-link-button" to="/pomoc/novosti">
        Što je novo
      </Link>
      <Link className="help-link-button" to="/pomoc">
        Moje prijave
      </Link>
      <label className="help-search">
        Pretraži upute
        <input
          type="search"
          placeholder="Pretraži upute, npr. otpremnica"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="help-actions" role="group" aria-label="Uloge za upute">
        <button
          type="button"
          className="help-chip"
          aria-pressed={!all}
          onClick={() => setAll(false)}
        >
          Za mene: {audience}
        </button>
        <button
          type="button"
          className="help-chip"
          aria-pressed={all}
          onClick={() => setAll(true)}
        >
          Sve uloge
        </button>
      </div>
      {loading && <p role="status">Učitavanje uputa…</p>}
      {failed && (
        <div role="alert">
          <p>Upute nisu dostupne. Pokušajte ponovno.</p>
          <Button variant="secondary" onClick={retry}>
            Pokušaj ponovno
          </Button>
        </div>
      )}
      {!loading && !failed && !visible.length && (
        <p>
          Nema pronađenih uputa. Pokušajte drugim riječima ili nam pošaljite
          pitanje.
        </p>
      )}
      <section className="help-card" aria-label="Upute">
        <h2>Upute</h2>
        {visible
          .filter((article) => article.screen !== "pomoc")
          .map((article) => (
            <details className="help-article" key={article.screen}>
              <summary>{article.title}</summary>
              <HelpSections article={article} />
            </details>
          ))}
      </section>
      <section className="help-card" aria-label="Česta pitanja">
        <h2>Česta pitanja</h2>
        {visible
          .filter((article) => article.screen === "pomoc")
          .map((article) => (
            <HelpSections key={article.screen} article={article} />
          ))}
      </section>
      {error && <p role="alert">{error}</p>}
      <div className="help-actions">
        <Button
          variant="secondary"
          disabled={busy || loading || failed}
          onClick={() => void download()}
        >
          {busy ? "Priprema ispisa…" : "Upute za ispis (PDF)"}
        </Button>
        <Button variant="secondary" onClick={() => openSupportQuestion()}>
          💬 I dalje ne znam
        </Button>
      </div>
    </div>
  );
}
